import type AggregateError from 'es-aggregate-error';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, DailyStat, Event, TAttributionModel } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import { Button, TableStatsCard, toast } from '@feathr/components';
import { useToggle } from '@feathr/hooks';
import type { IRachisMessage, ListResponse } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';
import { useStore } from '@feathr/report_components/state';

import DailyStatsTable from '../DailyStatsTable';
import ExportModal from '../ExportModal';

export interface IDailyStatsTableCardProps {
  attributionModel?: TAttributionModel;
  dailyStats: ListResponse<DailyStat>;
  end: string;
  object: Event | Campaign;
  showConversions?: boolean;
  showSpend?: boolean;
  start: string;
}

function DailyStatsTableCard({
  attributionModel,
  dailyStats,
  end,
  object,
  showConversions,
  showSpend,
  start,
}: Readonly<IDailyStatsTableCardProps>): JSX.Element {
  const { FacebookCampaignInsights } = useStore();
  const [showExportModal, toggleExportModal] = useToggle(false);
  const { t } = useTranslation();
  const className = object.attributes._cls;
  const isFacebookCampaign = className.includes('Facebook');

  async function handleConfirmExport(email: string): Promise<IRachisMessage | AggregateError> {
    const exportParams = new URLSearchParams({
      daily: '1',
      start,
      end,
      email,
    });
    const response = await wretch<IRachisMessage>(
      `${object.collection!.url('export', object.id)}?${exportParams.toString()}`,
      {
        method: 'GET',
        headers: object.collection!.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      return response.error;
    }
    return response.data;
  }

  async function handleSyncFacebookCampaignStats(): Promise<void> {
    const params = new URLSearchParams({
      campaign_id: object.id,
    });
    const response = await FacebookCampaignInsights.sync(params);
    if (isWretchError(response)) {
      toast(
        t('Something went wrong while syncing stats: {{- message}}', {
          message: response.error.message,
        }),
        { type: 'error' },
      );
    } else {
      toast(t('Campaign stats syncing is in progress. Please check back in a few minutes.'), {
        type: 'success',
      });
    }
  }

  const actions = [
    isFacebookCampaign && (
      <Button key={'sync'} onClick={handleSyncFacebookCampaignStats}>
        {t('Sync stats')}
      </Button>
    ),
    <Button key={'export'} onClick={toggleExportModal}>
      {t('Export to CSV')}
    </Button>,
  ];

  return (
    <>
      <TableStatsCard actions={actions} title={reportModuleLabels.includeDailyStatsTable}>
        <DailyStatsTable
          attributionModel={attributionModel ?? 'full'}
          isPending={dailyStats.isPending}
          showConversions={showConversions}
          showSpend={showSpend}
          stats={dailyStats}
        />
      </TableStatsCard>
      {showExportModal && (
        <ExportModal
          onClose={toggleExportModal}
          onConfirm={handleConfirmExport}
          title={t('Export Daily Stats')}
        />
      )}
    </>
  );
}

export default observer(DailyStatsTableCard);
